import HeaderSkeleton, {
  getHeaderManifest,
  type OwnProps as HeaderProps
} from '@carvana/header';
import { usePrevious } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { useContext, useEffect } from 'react';
import { commonEnvironment, environment } from '~/constants/environment';
import StaticContext from '~/context/common/StaticContext';
import { useFavorites } from '~/context/common/UserContext';
import type { FavoriteVehicle } from '~/contracts/common';
import { useClientSide } from '~/hooks/common';
import { wait } from '~/utilities';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';

function useShouldRefreshProfile(isLoggedIn: boolean): boolean {
  return usePrevious(isLoggedIn) !== isLoggedIn;
}

type OwnProps = {
  favorites?: FavoriteVehicle[];
  isLoggedIn: boolean;
};

function PageHeader(props: OwnProps) {
  const { removeHeader, serverCookies } = useContext(StaticContext);
  const shouldRefreshProfile = useShouldRefreshProfile(props.isLoggedIn);
  const isClientSide = useClientSide();
  const favorites = useFavorites();

  useEffect(() => {
    if (removeHeader) return;

    wait(100).then(() => {
      void MfeLoad({
        files: [
          [
            'HeaderModule',
            [
              {
                component: 'default',
                default: {
                  file: 'index.js',
                  manifest: getHeaderManifest(environment)
                }
              }
            ]
          ]
        ]
      });
    });
  }, [removeHeader]);

  const headerProps: HeaderProps = {
    cookies: serverCookies,
    environment: commonEnvironment,
    favorites: { favorites },
    shouldLoadPlugin: false,
    shouldRefreshProfile,
    useProvider: true
  };

  if (removeHeader) return null;

  return isClientSide ? (
    <MfeReactComponent
      component="default"
      fallback={{
        Component: <HeaderSkeleton {...headerProps} />,
        timeout: 30000
      }}
      loading={<HeaderSkeleton {...headerProps} />}
      module="HeaderModule"
      {...headerProps}
    />
  ) : (
    <HeaderSkeleton {...headerProps} />
  );
}

export default withErrorBoundary(PageHeader);
